<script setup>
import { ref, computed } from 'vue'
import { scaleQuantity, renderStep } from '../lib/quantity'
import FlowView from './FlowView.vue'

// Owns the serves state for a recipe page so the ingredient list AND the
// inline method quantities scale together — the cook never cross-references.
const props = defineProps({
  ingredients: { type: Array, default: () => [] },
  steps: { type: Array, default: () => [] },
  baseServes: { type: Number, default: 4 },
  prepMinutes: { type: Number, default: null },
  cookMinutes: { type: Number, default: null },
  cuisine: { type: String, default: null },
  stepImages: { type: Object, default: () => ({}) }, // position -> url
  flow: { type: Object, default: null }, // precomputed flow grid, or null
})

const target = ref(props.baseServes)

const initialView =
  props.flow && new URLSearchParams(window.location.search).get('view') === 'flow'
    ? 'flow'
    : 'classic'
const view = ref(initialView)

function setView(v) {
  view.value = v
  const url = new URL(window.location)
  if (v === 'flow') url.searchParams.set('view', 'flow')
  else url.searchParams.delete('view')
  history.replaceState(null, '', url)
}

function dec() { if (target.value > 1) target.value-- }
function inc() { if (target.value < 50) target.value++ }

const ingredientsByKey = computed(() =>
  Object.fromEntries(props.ingredients.map((i) => [i.key, i]))
)

const ingredientSections = computed(() => {
  const groups = []
  let current = null
  for (const ing of props.ingredients) {
    const name = ing.section || ''
    if (!current || current.name !== name) {
      current = { name, items: [] }
      groups.push(current)
    }
    current.items.push(ing)
  }
  // A plain unsectioned list gets the title "Ingredients"; named sections keep theirs.
  if (groups.length > 0 && !groups[0].name) groups[0].name = 'Ingredients'
  return groups
})

// Quantity + unit render in their own span (steadier weight, tabular
// figures) so the numbers column scans; the rest of the line stays quiet.
function ingredientQty(ing) {
  const qty = scaleQuantity(ing.quantity, props.baseServes, target.value)
  return [qty, ing.unit].filter(Boolean).join(' ')
}

function ingredientRest(ing) {
  return (ing.item || '') + (ing.note ? ` — ${ing.note}` : '')
}

function stepText(step) {
  return renderStep(step.body, ingredientsByKey.value, props.baseServes, target.value)
}
</script>

<template>
  <div>
    <div class="flex flex-wrap items-center gap-x-5 gap-y-2 mb-8 text-sm">
      <span class="flex items-center gap-2">
        <span class="eyebrow">Serves</span>
        <button type="button" class="btn btn-circle btn-xs btn-outline" aria-label="Fewer servings" @click="dec">−</button>
        <span class="text-xl font-semibold w-8 text-center">{{ target }}</span>
        <button type="button" class="btn btn-circle btn-xs btn-outline" aria-label="More servings" @click="inc">+</button>
        <span v-if="target !== baseServes" class="text-xs opacity-60">scaled from {{ baseServes }}</span>
      </span>
      <span v-if="prepMinutes" class="opacity-75">Prep {{ prepMinutes }} min</span>
      <span v-if="cookMinutes" class="opacity-75">Cook {{ cookMinutes }} min</span>
      <span v-if="cuisine" class="opacity-75">{{ cuisine }}</span>

      <span v-if="flow" class="join ml-auto">
        <button type="button" class="btn btn-xs join-item"
                :class="view === 'classic' ? 'btn-active' : 'btn-outline'"
                @click="setView('classic')">Classic</button>
        <button type="button" class="btn btn-xs join-item"
                :class="view === 'flow' ? 'btn-active' : 'btn-outline'"
                @click="setView('flow')">Flow</button>
      </span>
    </div>

    <FlowView
      v-if="flow && view === 'flow'"
      :flow="flow"
      :ingredients-by-key="ingredientsByKey"
      :base-serves="baseServes"
      :target="target"
    />

    <div v-else class="grid grid-cols-1 md:grid-cols-3 gap-10 border-t border-base-300 pt-10">
      <aside class="md:col-span-1">
        <div v-for="(group, i) in ingredientSections" :key="i" :class="i > 0 ? 'mt-8' : ''">
          <h3 class="section-head mb-4">{{ group.name }}</h3>
          <ul class="space-y-3">
            <li v-for="(ing, j) in group.items" :key="j" class="text-[1.0625rem] leading-relaxed">
              <span v-if="ingredientQty(ing)" class="ingredient-qty">{{ ingredientQty(ing) }}</span>
              {{ ingredientRest(ing) }}
            </li>
          </ul>
        </div>
      </aside>

      <section class="md:col-span-2">
        <h2 class="section-head mb-4">Method</h2>
        <ol class="space-y-7 max-w-prose">
          <li v-for="(step, i) in steps" :key="i" class="flex gap-4">
            <span class="text-2xl font-semibold tracking-tight text-primary/70 leading-none select-none">{{ i + 1 }}</span>
            <div class="flex-1">
              <h3 v-if="step.section && (i === 0 || steps[i - 1].section !== step.section)" class="eyebrow mb-1">
                {{ step.section }}
              </h3>
              <p class="text-[1.125rem] leading-[1.7]">{{ stepText(step) }}</p>
              <img v-if="stepImages[step.position]" :src="stepImages[step.position]"
                   class="mt-3 rounded-box max-w-sm" loading="lazy" :alt="`Step ${i + 1}`">
            </div>
          </li>
        </ol>
      </section>
    </div>
  </div>
</template>
